import { useQuery } from '@tanstack/react-query'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { LocalDate } from '../../platform/localDate'
import type { TxnQuery } from './filters'
import { fetchPage, sameExceptPage, type TxnPage } from './useTransactions'

export type ScopedTxnQuery = {
  start: LocalDate
  end: LocalDate
  kind: 'expense' | 'income'
  categoryId?: string
  groupId?: string
  merchantId?: string
  merchantName?: string
  uncategorized?: true
  /** Rows with no merchant id: the Cashflow "No merchant" row (backend 512). */
  noMerchant?: true
  page: number
}

export const SCOPED_PAGE_SIZE = 100

/** `cashflow_kind` needs both dates and allows one scope; the report's own account scope applies. */
function toScopedQuery(q: ScopedTxnQuery): TxnQuery {
  return {
    page: q.page,
    page_size: SCOPED_PAGE_SIZE,
    start_date: q.start,
    end_date: q.end,
    cashflow_kind: q.kind,
    ...(q.categoryId === undefined ? {} : { category_id: q.categoryId }),
    ...(q.groupId === undefined ? {} : { group_id: q.groupId }),
    ...(q.merchantId === undefined ? {} : { merchant_id: q.merchantId }),
    ...(q.merchantName === undefined ? {} : { merchant_name: q.merchantName }),
    ...(q.uncategorized === undefined ? {} : { uncategorized: true }),
    ...(q.noMerchant === undefined ? {} : { no_merchant: true }),
  }
}

/**
 * The rows behind one cashflow figure: one page of 100 under `keys.transactions.list`, so any
 * transaction write invalidates it. A LIVE surface, like `useTransactions`; only a page change
 * keeps the previous page on screen.
 */
export function useScopedTransactions(q: ScopedTxnQuery): AsyncState<TxnPage> & { fetching: boolean } {
  const query = toScopedQuery(q)
  const result = useQuery({
    queryKey: keys.transactions.list(query),
    queryFn: ({ signal }) => fetchPage(query, signal),
    placeholderData: (previous, previousQuery) =>
      previousQuery !== undefined && sameExceptPage(previousQuery.queryKey[2], query) ? previous : undefined,
  })
  const state = fromQuery(result, {
    message: () => 'Transactions could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
  })
  return { ...state, fetching: result.isFetching }
}
